import { usePostHog } from "@posthog/react";
import React, {
  Dispatch,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { Action, ActionType, ConnectState, Steps } from "./types";

export const ConnectContext = createContext<ConnectState | null>(null);
export const ConnectDispatchContext = createContext<Dispatch<Action> | null>(
  null,
);

interface ConnectProviderProps {
  children: React.ReactNode;
}

const initialState: ConnectState = {
  currentStep: Steps.Init,
  progress: 0,
};

const getStepProgress = (step: Steps) => {
  const stepCount = 5;
  const currentStep = step.valueOf();
  return (currentStep / stepCount) * 100;
};

const reducer = (state: ConnectState, action: Action): ConnectState => {
  switch (action.type) {
    case ActionType.Back:
      return {
        ...state,
        currentStep: state.currentStep - 1,
        progress: getStepProgress(state.currentStep - 1),
      };
    case ActionType.Reset:
      return {
        ...state,
        currentStep: Steps.Init,
        progress: 0,
      };
    case ActionType.InitializeSession:
      return {
        ...initialState,
        currentStep: Steps.InstitutionSelect,
        progress: getStepProgress(Steps.InstitutionSelect),
        applicationId: action.payload.applicationId,
        externalUserId: action.payload.externalUserId,
        appConfig: action.payload.appConfig,
        connectSessionId: action.payload.sessionId,
        accountSelectionMode: action.payload.accountSelectionMode,
      };
    case ActionType.InitializeReconnectSession:
      return {
        ...initialState,
        currentStep: Steps.Authentication,
        progress: getStepProgress(Steps.Authentication),
        isReconnect: true,
        reconnectConnectionId: action.payload.connectionId,
        connectSessionId: action.payload.sessionId,
        appConfig: action.payload.appConfig,
        institution: action.payload.institution,
        accountSelectionMode: action.payload.accountSelectionMode,
      };
    case ActionType.SetInstitution:
      return {
        ...state,
        currentStep: Steps.Permissions,
        progress: getStepProgress(Steps.Permissions),
        institution: action.payload,
      };
    case ActionType.AcceptPermissions:
      return {
        ...state,
        currentStep: Steps.Authentication,
        progress: getStepProgress(Steps.Authentication),
      };
    case ActionType.SetAuthenticated:
      return {
        ...state,
        currentStep: Steps.AccountSelect,
        progress: getStepProgress(Steps.AccountSelect),
        auth: {
          ...state.auth,
          isAuthenticated: true,
          isVerified: false,
          needsVerification: action.payload.needsVerification,
        },
      };
    case ActionType.SetVerified:
      return {
        ...state,
        currentStep: Steps.AccountSelect,
        progress: getStepProgress(Steps.AccountSelect),
        auth: {
          ...state.auth!,
          isVerified: true,
        },
      };
    case ActionType.SetSelectedAccounts:
      return {
        ...state,
        currentStep: Steps.Finished,
        progress: getStepProgress(Steps.Finished),
        connectionId: action.payload.connectionId,
        accounts: {
          selected: action.payload.accounts,
        },
        user: action.payload.user,
        scopedToken: action.payload.scopedToken,
      };
    case ActionType.CompleteReconnect:
      return {
        ...state,
        currentStep: Steps.Finished,
        progress: getStepProgress(Steps.Finished),
        connectionId: action.payload.connectionId,
      };
    default:
      throw Error("Unknown action: " + action.type);
  }
};

export const useConnectState = () => {
  const state = useContext(ConnectContext);
  return state!;
};

export const useConnectDispatch = () => {
  const dispatch = useContext(ConnectDispatchContext);
  return dispatch!;
};

// Component to handle PostHog session properties
const PostHogSessionProperties: React.FC = () => {
  const state = useConnectState();
  const posthog = usePostHog();

  useEffect(() => {
    if (state.connectSessionId && state.applicationId) {
      // Register properties to be sent with all subsequent events
      // This allows filtering recordings by connect session without overriding user identity
      posthog.register({
        connect_session_id: state.connectSessionId,
        application_id: state.applicationId,
        is_sandbox: state.appConfig?.isSandbox ?? false,
        is_reconnect: state.isReconnect ?? false,
      });
      posthog.group("application", state.applicationId);
    }
  }, [
    posthog,
    state.connectSessionId,
    state.applicationId,
    state.appConfig?.isSandbox,
    state.isReconnect,
  ]);

  return null;
};

export const ConnectProvider: React.FC<ConnectProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <ConnectContext.Provider value={state}>
      <ConnectDispatchContext.Provider value={dispatch}>
        <PostHogSessionProperties />
        {children}
      </ConnectDispatchContext.Provider>
    </ConnectContext.Provider>
  );
};
